import React from "react";

/**
 * CosmicLoader - Beautiful orbiting planet loading animation
 *
 * A cosmic-themed loader with a golden sun center and an orbiting teal planet.
 * Matches the Think platform's space aesthetic.
 *
 * @param {string} text - Loading text to display (default: "Loading...")
 * @param {string} size - Size variant: "sm" | "md" | "lg" (default: "md")
 * @param {boolean} showText - Whether to show the loading text (default: true)
 * @param {string} className - Additional CSS classes for the container
 */
const CosmicLoader = ({
  text = "Loading...",
  size = "md",
  showText = true,
  className = "",
}) => {
  // Size configurations
  const sizes = {
    sm: {
      container: "w-12 h-12",
      sun: "w-5 h-5",
      planet: "w-2 h-2",
      text: "text-xs",
      gap: "gap-3",
    },
    md: {
      container: "w-20 h-20",
      sun: "w-8 h-8",
      planet: "w-3 h-3",
      text: "text-sm",
      gap: "gap-6",
    },
    lg: {
      container: "w-28 h-28",
      sun: "w-12 h-12",
      planet: "w-4 h-4",
      text: "text-base",
      gap: "gap-8",
    },
  };

  const s = sizes[size] || sizes.md;

  return (
    <div
      className={`flex flex-col items-center justify-center ${s.gap} ${className}`}
    >
      {/* Orbiting loader */}
      <div className={`relative ${s.container}`}>
        {/* Center sun */}
        <div
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${s.sun} rounded-full cosmic-loader-sun`}
          style={{
            background:
              "radial-gradient(circle at 30% 30%, #FFF5E0 0%, #FFD700 40%, #FF8C00 100%)",
            boxShadow: "0 0 30px rgba(255, 215, 0, 0.5)",
          }}
        />

        {/* Orbiting planet */}
        <div className="absolute inset-0 cosmic-loader-orbit">
          <div
            className={`absolute left-1/2 -translate-x-1/2 top-0 ${s.planet} rounded-full`}
            style={{
              background:
                "radial-gradient(circle at 30% 30%, #ABEBF7 0%, #6BC5D9 100%)",
              boxShadow: "0 0 10px rgba(171, 235, 247, 0.5)",
            }}
          />
        </div>

        {/* Orbit ring */}
        <div className="absolute inset-0 rounded-full border border-white/10 cosmic-loader-ring" />
      </div>

      {/* Loading text */}
      {showText && (
        <p
          className={`text-white/60 ${s.text} uppercase tracking-widest font-medium`}
        >
          {text}
        </p>
      )}

      {/* Keyframe animations */}
      <style>{`
        @keyframes cosmicSunPulse {
          0%, 100% {
            transform: translate(-50%, -50%) scale(1);
            box-shadow: 0 0 30px rgba(255, 215, 0, 0.5);
          }
          50% {
            transform: translate(-50%, -50%) scale(1.05);
            box-shadow: 0 0 40px rgba(255, 215, 0, 0.7), 0 0 60px rgba(255, 140, 0, 0.4);
          }
        }

        @keyframes cosmicOrbit {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes cosmicRingPulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.02); }
        }

        .cosmic-loader-sun {
          animation: cosmicSunPulse 2s ease-in-out infinite;
        }

        .cosmic-loader-orbit {
          animation: cosmicOrbit 2s linear infinite;
        }

        .cosmic-loader-ring {
          animation: cosmicRingPulse 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

/**
 * CosmicLoaderFullScreen - Full screen version with centered loader
 * Use this to replace the basic Spinner component
 */
export const CosmicLoaderFullScreen = ({
  text = "Loading...",
  className = "",
}) => (
  <div
    className={`flex justify-center items-center w-full h-screen ${className}`}
  >
    <CosmicLoader text={text} size="md" />
  </div>
);

export default CosmicLoader;
